import React, { useState, useEffect, useCallback } from 'react';
import { schedulingService, schedulingUtils } from '../services/schedulingService';

const SchedulingSuggestions = ({
  schedulingRequest,
  onSelectSuggestion,
  onAutoSchedule,
  showAutoScheduleButton = true,
  autoSaveOnSelect = false,  // NEW: If true, automatically save event when suggestion is selected
  maxSuggestions = 5,
  className = ''
}) => {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [savingSlot, setSavingSlot] = useState(null); // Track which slot is being saved

  const fetchSuggestions = useCallback(async () => {
    if (!schedulingRequest) return;

    setLoading(true);
    setError(null);

    try {
      const result = await schedulingService.findAvailableSlots(schedulingRequest, maxSuggestions);
      setSuggestions(result || []);
    } catch (err) {
      console.error('Error fetching scheduling suggestions:', err);
      setError('Failed to find available time slots. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [schedulingRequest, maxSuggestions]);

  useEffect(() => {
    if (schedulingRequest) {
      fetchSuggestions();
    }
  }, [schedulingRequest, fetchSuggestions]);

  const handleAutoSchedule = async () => {
    if (!schedulingRequest) return;

    try {
      setLoading(true);
      const result = await schedulingService.autoSchedule(schedulingRequest, true);
      
      if (result.success && result.auto_created) {
        if (onAutoSchedule) {
          onAutoSchedule(result);
        }
      } else {
        setError(result.message || 'Auto-scheduling failed');
      }
    } catch (err) {
      console.error('Error auto-scheduling:', err);
      setError('Auto-scheduling failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectSuggestion = async (suggestion, index) => {
    if (autoSaveOnSelect && schedulingRequest) {
      // Auto-save the event when suggestion is selected
      try {
        setSavingSlot(index);
        setError(null);
        const result = await schedulingService.scheduleWithSpecificSlot(
          schedulingRequest,
          suggestion.start_datetime,
          suggestion.end_datetime,
          false // Don't force if there are conflicts
        );
        
        if (onSelectSuggestion) {
          onSelectSuggestion({ ...suggestion, event_id: result.id, created: true });
        }
      } catch (err) {
        console.error('Error creating event from suggestion:', err);
        setError('Failed to create event. Please try again.');
      } finally {
        setSavingSlot(null);
      }
    } else {
      // Just pass the suggestion to parent (existing behavior)
      if (onSelectSuggestion) {
        onSelectSuggestion(suggestion);
      }
    }
  };

  const getConfidenceBadge = (confidence) => {
    const percent = Math.round(confidence * 100);
    let bgColor = 'bg-gray-100';
    let textColor = 'text-gray-700';
    
    if (confidence >= 0.8) {
      bgColor = 'bg-green-100';
      textColor = 'text-green-700';
    } else if (confidence >= 0.6) {
      bgColor = 'bg-yellow-100';
      textColor = 'text-yellow-700';
    } else {
      bgColor = 'bg-red-100';
      textColor = 'text-red-700';
    }

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${bgColor} ${textColor}`}>
        {percent}% match
      </span>
    );
  };

  if (!schedulingRequest) {
    return (
      <div className={`bg-gray-50 p-4 rounded-lg ${className}`}>
        <p className="text-gray-500 text-center">Enter event details to see scheduling suggestions</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className={`bg-white p-6 rounded-lg border ${className}`}>
        <div className="flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-500"></div>
          <span className="ml-3 text-gray-600">Finding available time slots...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`bg-red-50 border border-red-200 rounded-lg p-4 ${className}`}>
        <div className="flex items-center">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">
              Error finding time slots
            </h3>
            <div className="mt-2 text-sm text-red-700">
              <p>{error}</p>
            </div>
            <div className="mt-3">
              <button
                onClick={fetchSuggestions}
                className="bg-red-100 px-3 py-2 rounded-md text-sm font-medium text-red-800 hover:bg-red-200"
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (suggestions.length === 0) {
    return (
      <div className={`bg-yellow-50 border border-yellow-200 rounded-lg p-4 ${className}`}>
        <div className="flex items-center">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-yellow-800">
              No available time slots found
            </h3>
            <div className="mt-2 text-sm text-yellow-700">
              <p>Try adjusting your date range, duration, or requirements to find available times.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-white border rounded-lg ${className}`}>
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-medium text-gray-900">
            Suggested Time Slots
          </h3>
          {showAutoScheduleButton && suggestions.length > 0 && (
            <button
              onClick={handleAutoSchedule}
              disabled={loading}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-yellow-500 hover:bg-yellow-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
              Auto-Schedule Best Option
            </button>
          )}
        </div>
        <p className="mt-1 text-sm text-gray-500">
          {suggestions.length} suggestion{suggestions.length !== 1 ? 's' : ''} found for "{schedulingRequest.title}"
        </p>
      </div>

      <div className="divide-y divide-gray-200">
        {suggestions.map((suggestion, index) => {
          const formatted = schedulingUtils.formatSuggestion(suggestion);
          const reasonText = schedulingUtils.getReasonText(suggestion.reason);
          
          return (
            <div
              key={index}
              className={`px-6 py-4 transition-colors duration-150 ${
                savingSlot === index 
                  ? 'bg-gray-50 cursor-wait' 
                  : 'hover:bg-gray-50 cursor-pointer'
              }`}
              onClick={() => savingSlot === null && handleSelectSuggestion(suggestion, index)}
            >
              <div className="flex items-center justify-between">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center">
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900">
                        {formatted.formatted_date}
                      </p>
                      <p className="text-lg font-semibold text-gray-900">
                        {formatted.formatted_time}
                      </p>
                      <p className="text-sm text-gray-500 mt-1">
                        {reasonText}
                        {suggestion.buffer_before_minutes > 0 && (
                          <span className="ml-2 text-xs text-yellow-600">
                            +{suggestion.buffer_before_minutes}min prep
                          </span>
                        )}
                        {suggestion.buffer_after_minutes > 0 && (
                          <span className="ml-2 text-xs text-yellow-600">
                            +{suggestion.buffer_after_minutes}min buffer
                          </span>
                        )}
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center space-x-3">
                  {getConfidenceBadge(suggestion.confidence)}
                  {savingSlot === index ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-yellow-500"></div>
                  ) : (
                    <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  )}
                </div>
              </div>
              
              {index === 0 && (
                <div className="mt-2">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                    ⭐ Best Match
                  </span>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default SchedulingSuggestions;